/** Persist the agent's recent call history in the browser so it survives a page
 *  refresh — the same UX the RingCentral Embeddable widget gives (it keeps call
 *  history in browser storage). Without this, the Calls + Contacts tabs reset to
 *  empty on every reload.
 *
 *  Security posture:
 *   - Scoped per agent id (the signed-in username): a different agent signing in
 *     on the same browser loads their OWN key and never sees another's calls.
 *   - Only the minimal HistoryEntry fields are stored (number/name/direction/
 *     outcome/duration/time) — never call notes, dispositions, tokens, or media.
 *   - Capped so storage can't grow unbounded.
 *   - Best-effort: private-mode / quota / disabled storage silently degrades to
 *     in-memory only (never throws into the call flow).
 */
import type { HistoryEntry } from "../telephony/types";

const MAX = 50;
const key = (agentId: string) => `ccv2.cti.history.${agentId}`;

/** Keep only the safe, display-only fields — defence in depth against a future
 *  HistoryEntry gaining a sensitive field that shouldn't hit localStorage. */
function sanitize(e: HistoryEntry): HistoryEntry {
  return {
    id: e.id,
    direction: e.direction,
    contact: { number: e.contact.number, name: e.contact.name, company: e.contact.company },
    startedAtMs: e.startedAtMs,
    durationSec: e.durationSec,
    outcome: e.outcome,
  };
}

export function loadHistory(agentId: string): HistoryEntry[] {
  if (!agentId) return [];
  try {
    const raw = localStorage.getItem(key(agentId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as HistoryEntry[]).slice(0, MAX) : [];
  } catch {
    return [];
  }
}

export function saveHistory(agentId: string, history: HistoryEntry[]): void {
  if (!agentId) return;
  try {
    localStorage.setItem(key(agentId), JSON.stringify(history.slice(0, MAX).map(sanitize)));
  } catch {
    /* quota exceeded / storage disabled → in-memory only */
  }
}
